/**
 * Kuwait Currency Converter for Shopify
 * Automatically detects Kuwait location and displays prices in KWD
 * Also allows manual KWD selection from mobile menu
 * Works in theme preview and live mode
 */

(function () {
    'use strict';

    const KuwaitCurrency = {
        config: {
            targetCurrency: 'KWD',
            countryCode: 'KW',
            storageKey: 'user_country',
            rateStorageKey: 'kwd_exchange_rate',
            rateCacheHours: 24,
            fallbackRate: 0.307, // USD to KWD (updated rate)
        },

        isInitialized: false,
        currentRate: null,

        // Initialize
        init: function () {
            // Watch for country changes in Shopify's localization form
            this.watchCountryChanges();

            // Check if user is in Kuwait (from Shopify Markets)
            const isInKuwait = this.detectKuwaitLocation();

            // Check if KWD was manually selected
            const isKWDManuallySelected = localStorage.getItem('force_kwd') === 'true';
            const isSwitchingAway = localStorage.getItem('switching_away_from_kwd') === 'true';

            // Initialize KWD if:
            // 1. User is in Kuwait (automatic detection), OR
            // 2. KWD was manually selected from menu
            // AND not switching away from KWD
            if ((isInKuwait || isKWDManuallySelected) && !isSwitchingAway) {
                this.initializeKuwaitCurrency();
            } else if (isSwitchingAway) {
                // Clear all KWD data when switching away
                localStorage.removeItem('user_country');
                localStorage.removeItem('force_kwd');
                localStorage.removeItem('switching_away_from_kwd');

                // Restore original prices
                this.restoreOriginalPrices();
            }
        },

        // Detect if user is in Kuwait based on Shopify Markets data
        detectKuwaitLocation: function () {
            // Check if Shopify Markets detected Kuwait
            if (window.shopifyMarketCountry === 'KW') {
                return true;
            }

            // Fallback: Check if there's a meta tag indicating Kuwait
            const metaTag = document.querySelector('meta[name="shopify-country"]');
            if (metaTag && metaTag.getAttribute('content') === 'KW') {
                return true;
            }

            return false;
        },

        // Watch for country changes in localization selector
        watchCountryChanges: function () {
            const self = this;

            // Watch for form submissions (localization form)
            document.addEventListener('submit', function (e) {
                if (e.target && e.target.id && e.target.id.includes('localization')) {
                    // Clear KWD settings when form is submitted
                    localStorage.removeItem(self.config.storageKey);
                    localStorage.removeItem('force_kwd');
                    localStorage.removeItem('switching_away_from_kwd');
                }
            });

            // Watch for clicks on country/locale options
            document.addEventListener('click', function (e) {
                const target = e.target;

                // Handle KWD custom option clicks
                const kwdElement = target.closest('[data-kwd-custom-option]');
                if (kwdElement) {
                    e.preventDefault();
                    e.stopPropagation();

                    // Set Kuwait as selected
                    localStorage.setItem(self.config.storageKey, self.config.countryCode);
                    localStorage.setItem('force_kwd', 'true');

                    // Close mobile menu if it's open
                    const mobileMenu = document.getElementById('page-menu');
                    if (mobileMenu && mobileMenu.classList.contains('visible')) {
                        mobileMenu.classList.remove('visible');
                    }

                    // Close any open dropdowns
                    const disclosureList = kwdElement.closest('.disclosure').querySelector('.disclosure-list');
                    if (disclosureList) {
                        disclosureList.classList.remove('disclosure-list--visible');
                    }

                    // Reload page to ensure clean state
                    setTimeout(function () {
                        location.reload();
                    }, 100);
                    return;
                }

                // Handle switching away from KWD - IMMEDIATELY clear KWD settings
                if (target && target.getAttribute('data-disclosure-option') && target.getAttribute('data-value') !== 'KWD') {
                    // Set flag that we're switching away from KWD
                    localStorage.setItem('switching_away_from_kwd', 'true');

                    // Clear KWD settings immediately
                    localStorage.removeItem(self.config.storageKey);
                    localStorage.removeItem('force_kwd');

                    // Close mobile menu if open
                    const mobileMenu = document.getElementById('page-menu');
                    if (mobileMenu && mobileMenu.classList.contains('visible')) {
                        mobileMenu.classList.remove('visible');
                    }

                    // Don't prevent default - let Shopify handle the currency change normally
                    // The theme's Disclosure class will handle the form submission
                }
            });
        },

        // Initialize KWD conversion
        initializeKuwaitCurrency: function () {
            if (this.isInitialized) {
                return;
            }

            this.isInitialized = true;

            // Signal to other scripts (header announcement bar)
            // that Kuwait/KWD is active — they may have run before us due to defer ordering.
            window.shopifyIsKuwait = true;
            try { document.dispatchEvent(new CustomEvent('kwd:active')); } catch(e) {}

            this.getExchangeRate()
                .then(rate => {
                    this.currentRate = rate;
                    this.convertAllPrices(rate);
                    this.watchForDynamicContent(rate);
                    this.updateMobileMenuDisplay();
                });
        },

        // Get exchange rate
        getExchangeRate: function () {
            const self = this;

            return new Promise((resolve) => {
                // Check cache
                const cachedData = localStorage.getItem(this.config.rateStorageKey);

                if (cachedData) {
                    try {
                        const parsed = JSON.parse(cachedData);
                        const hoursSinceCache = (Date.now() - parsed.timestamp) / (1000 * 60 * 60);

                        if (hoursSinceCache < this.config.rateCacheHours) {
                            resolve(parsed.rate);
                            return;
                        }
                    } catch (e) {
                        // Silently fail
                    }
                }

                // Fetch fresh rate
                const shopCurrency = window.Shopify?.currency?.active || 'USD';

                fetch(`https://api.exchangerate-api.com/v4/latest/${shopCurrency}`)
                    .then(response => response.json())
                    .then(data => {
                        if (data.rates && data.rates.KWD) {
                            const rate = data.rates.KWD;

                            localStorage.setItem(self.config.rateStorageKey, JSON.stringify({
                                rate: rate,
                                timestamp: Date.now()
                            }));

                            resolve(rate);
                        } else {
                            resolve(self.config.fallbackRate);
                        }
                    })
                    .catch(() => {
                        resolve(self.config.fallbackRate);
                    });
            });
        },

        // Convert all prices on page
        convertAllPrices: function (exchangeRate) {
            const self = this;

            // Find all price elements
            const selectors = [
                '.theme-money',
                '.money',
                '[data-product-price]',
                '.price .current-price',
                '.product-price',
                '.variant-price'
            ];

            selectors.forEach(function (selector) {
                const elements = document.querySelectorAll(selector + ':not([data-kwd-converted])');

                elements.forEach(function (element) {
                    self.convertPriceElement(element, exchangeRate);
                });
            });

            // Also check for prices in text format like "$49.00"
            this.convertTextPrices(exchangeRate);
        },

        // Convert a single price element
        convertPriceElement: function (element, exchangeRate) {
            if (!element || element.dataset.kwdConverted === 'true') {
                return false;
            }

            const originalText = element.textContent.trim();
            if (!originalText) return false;

            // Extract numeric value
            const numericValue = this.extractNumericValue(originalText);

            if (numericValue > 0) {
                const kwdValue = numericValue * exchangeRate;
                const formattedPrice = this.formatKWD(kwdValue);

                // Store original
                element.dataset.originalPrice = originalText;
                element.dataset.kwdConverted = 'true';
                element.textContent = formattedPrice;

                return true;
            }

            return false;
        },

        // Convert prices in plain text
        convertTextPrices: function (exchangeRate) {
            // This handles cases where price is not in a specific class
            const walker = document.createTreeWalker(
                document.body,
                NodeFilter.SHOW_TEXT,
                null,
                false
            );

            const textNodes = [];
            let node;

            while (node = walker.nextNode()) {
                if (node.nodeValue && node.nodeValue.match(/\$\d+/)) {
                    textNodes.push(node);
                }
            }

            textNodes.forEach(node => {
                if (!node.parentElement.dataset.kwdConverted) {
                    const original = node.nodeValue;
                    const converted = this.convertTextPriceString(original, exchangeRate);
                    if (converted !== original) {
                        node.nodeValue = converted;
                        if (node.parentElement) {
                            node.parentElement.dataset.kwdConverted = 'true';
                        }
                    }
                }
            });
        },

        // Convert price string
        convertTextPriceString: function (text, exchangeRate) {
            return text.replace(/\$(\d+(?:\.\d{2})?)/g, (match, price) => {
                const numValue = parseFloat(price);
                const kwdValue = numValue * exchangeRate;
                return this.formatKWD(kwdValue);
            });
        },

        // Extract numeric value from price string
        extractNumericValue: function (priceString) {
            // Remove all non-numeric except dots and commas
            const cleaned = priceString.replace(/[^0-9.,]/g, '');

            if (!cleaned) return 0;

            // Handle different formats
            let numeric;

            if (cleaned.includes(',') && cleaned.includes('.')) {
                const lastComma = cleaned.lastIndexOf(',');
                const lastDot = cleaned.lastIndexOf('.');

                if (lastDot > lastComma) {
                    numeric = cleaned.replace(/,/g, '');
                } else {
                    numeric = cleaned.replace(/\./g, '').replace(',', '.');
                }
            } else if (cleaned.includes(',')) {
                const parts = cleaned.split(',');
                if (parts[parts.length - 1].length === 2) {
                    numeric = cleaned.replace(',', '.');
                } else {
                    numeric = cleaned.replace(/,/g, '');
                }
            } else {
                numeric = cleaned;
            }

            return parseFloat(numeric) || 0;
        },

        // Format price in KWD
        formatKWD: function (amount) {
            // Round up to nearest whole number
            const roundedAmount = Math.ceil(amount);
            return roundedAmount + ' KWD';
        },

        // Watch for dynamically added content
        watchForDynamicContent: function (exchangeRate) {
            const self = this;

            // Cart drawer re-renders its HTML on cart:refresh — re-run full conversion
            document.addEventListener('cart:refresh', function () {
                setTimeout(function () {
                    if (self.currentRate) {
                        // Clear converted flags inside cart drawer so prices get re-converted
                        var cartDrawer = document.getElementById('cart-drawer');
                        if (cartDrawer) {
                            cartDrawer.querySelectorAll('[data-kwd-converted]').forEach(function (el) {
                                el.removeAttribute('data-kwd-converted');
                                el.removeAttribute('data-original-price');
                            });
                        }
                        self.convertAllPrices(self.currentRate);
                    }
                }, 600);
            });

            const observer = new MutationObserver(function (mutations) {
                let needsConversion = false;
                mutations.forEach(function (mutation) {
                    mutation.addedNodes.forEach(function (node) {
                        if (node.nodeType === 1) {
                            // Check the node itself
                            if (node.classList && node.classList.contains('theme-money')) {
                                self.convertPriceElement(node, exchangeRate);
                            }

                            // Check children for .theme-money elements
                            const children = node.querySelectorAll ?
                                node.querySelectorAll('.theme-money:not([data-kwd-converted])') : [];
                            children.forEach(function (child) {
                                self.convertPriceElement(child, exchangeRate);
                            });

                            // If cart drawer content was replaced, schedule a full re-conversion
                            if (node.classList && (
                                node.classList.contains('cart-drawer__content') ||
                                node.classList.contains('cart-drawer__body') ||
                                node.classList.contains('cart-drawer__footer')
                            )) {
                                needsConversion = true;
                            }
                        }
                    });
                });
                if (needsConversion) {
                    setTimeout(function () { self.convertAllPrices(exchangeRate); }, 50);
                }
            });

            observer.observe(document.body, {
                childList: true,
                subtree: true
            });
        },

        // Update mobile menu display to show current currency
        updateMobileMenuDisplay: function () {
            // Check if KWD is active (either by location detection or manual selection)
            const isInKuwait = this.detectKuwaitLocation();
            const isKWDManuallySelected = localStorage.getItem('force_kwd') === 'true';
            const isKWD = isInKuwait || isKWDManuallySelected;

            if (isKWD) {
                // Find all currency disclosure toggles and update them to show KWD
                const toggles = document.querySelectorAll('.disclosure[data-disclosure-currency] .disclosure__toggle');
                toggles.forEach(function (toggle) {
                    if (toggle && !toggle.textContent.includes('KWD')) {
                        toggle.textContent = 'KWD';
                        toggle.innerHTML = 'KWD <span class="disclosure__toggle-arrow" role="presentation">▼</span>';
                    }
                });

                // Also update any mobile menu currency displays
                const mobileMenus = document.querySelectorAll('#page-menu .disclosure[data-disclosure-currency] .disclosure__toggle');
                mobileMenus.forEach(function (toggle) {
                    if (toggle && !toggle.textContent.includes('KWD')) {
                        toggle.textContent = 'KWD';
                        toggle.innerHTML = 'KWD <span class="disclosure__toggle-arrow" role="presentation">▼</span>';
                    }
                });
            }
        },

        // Close mobile menu
        closeMobileMenu: function () {
            const mobileMenu = document.getElementById('page-menu');
            if (mobileMenu && mobileMenu.classList.contains('visible')) {
                mobileMenu.classList.remove('visible');
            }
        },

        // Restore original prices when switching away from KWD
        restoreOriginalPrices: function () {
            const convertedElements = document.querySelectorAll('[data-kwd-converted="true"]');

            convertedElements.forEach(function (element) {
                const originalPrice = element.dataset.originalPrice;
                if (originalPrice) {
                    element.textContent = originalPrice;
                    element.removeAttribute('data-kwd-converted');
                    element.removeAttribute('data-original-price');
                }
            });

            // Reset initialization flag
            this.isInitialized = false;
        }
    };

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function () {
            KuwaitCurrency.init();
        });
    } else {
        KuwaitCurrency.init();
    }

    // Expose to window for manual control
    window.KuwaitCurrency = KuwaitCurrency;

    // Utility function to clear KWD settings
    window.clearKWD = function () {
        localStorage.removeItem('user_country');
        localStorage.removeItem('kwd_exchange_rate');
        localStorage.removeItem('force_kwd');
        localStorage.removeItem('switching_away_from_kwd');
        location.reload();
    };

    // Utility function to manually trigger KWD conversion
    window.triggerKWD = function () {
        localStorage.setItem('user_country', 'KW');
        localStorage.setItem('force_kwd', 'true');
        KuwaitCurrency.initializeKuwaitCurrency();
    };

    // Utility function to check current status
    window.checkKWDStatus = function () {
        const isInKuwait = KuwaitCurrency.detectKuwaitLocation();
        const isKWDManuallySelected = localStorage.getItem('force_kwd') === 'true';
        const isKWD = isInKuwait || isKWDManuallySelected;
        const rate = localStorage.getItem('kwd_exchange_rate');
        const isInitialized = KuwaitCurrency.isInitialized;

        return {
            isInKuwait: isInKuwait,
            isKWDManuallySelected: isKWDManuallySelected,
            isKWD: isKWD,
            rate: rate,
            isInitialized: isInitialized
        };
    };

    // Utility function to close mobile menu
    window.closeMobileMenu = function () {
        KuwaitCurrency.closeMobileMenu();
    };

})();